import React from "react";
import { useTranslation } from "react-i18next";
import { StyledModal, StyledLabel, CheckboxIcon } from "@aureskonnect/react-ui";
import { ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { useSnapshot } from "valtio";
import { mutate } from "swr";
import { toast } from "react-toastify";
import { uuid } from "uuidv4";

import { setKey } from "./store";
import { store } from "@components/VerticalLayout/store";
import { capitalize } from "@helpers/general";

import { CustomMainColorButton } from "@components/Common/CustomMainColorButton";
import { CustomSecondaryColorButton } from "@components/Common/CustomSecondaryColorButton";
import { useFetchGlobal } from "../../../api/hooks";

type ConfirmationDeleteLocationType = {
    rowData: any;
    isOpened: boolean;
    setIsOpened: Function;
};

export function ConfirmationDeleteLocation({
    rowData,
    isOpened,
    setIsOpened,
}: ConfirmationDeleteLocationType) {
    const { t } = useTranslation();
    const { fetchGlobal } = useFetchGlobal();
    const { franchiseID } = useSnapshot(store);
    const [canValidate, setCanValidate] = React.useState(true);

    async function handleValidate() {
        setCanValidate(false);
        toast.dismiss();
        const apiUrlAdd = `${process.env.REACT_APP_API_V2_URL}/settings/general/locationManagement/delete`;
        try {
            const data: any = await fetchGlobal(apiUrlAdd, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    id: rowData.Id,
                    shopId: rowData.shopId,
                    franchiseId: franchiseID,
                }),
            });
            mutate(apiUrlAdd, data);
            if (data.error) {
                throw Error("error");
            }

            toast.success(`${t("The Location was successfully deleted")}`, {
                position: toast.POSITION.TOP_CENTER,
                theme: "colored",
                icon: <CheckboxIcon height={25} width={25} fill="white" />,
            });
            setKey(uuid());
            setIsOpened(false);
            setCanValidate(true);
        } catch (e) {
            setCanValidate(true);
            toast.error(`${t("There's an error")}`, {
                position: toast.POSITION.TOP_CENTER,
                autoClose: 2000,
                theme: "colored",
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
        }
    }

    return (
        <StyledModal toggle={true} isOpen={isOpened} centered>
            <ModalHeader
                className="text-capitalize"
                toggle={() => {
                    setIsOpened(false);
                }}
            >
                <span>{capitalize(t("Alert"))}</span>
            </ModalHeader>
            <ModalBody>
                <StyledLabel
                    className="pl-3 m-0"
                    style={{ whiteSpace: "pre-line" }}
                >
                    {t(
                        "Do you want to confirm the deletion of the selected location?"
                    )}
                </StyledLabel>
            </ModalBody>
            <ModalFooter className="border-top-0 ">
                <React.Fragment>
                    <CustomSecondaryColorButton
                        outline
                        variant="light"
                        onClick={() => {
                            setIsOpened(false);
                        }}
                        rounded
                    >
                        {t("Cancel")}
                    </CustomSecondaryColorButton>
                    <CustomMainColorButton
                        rounded
                        className="mr-2"
                        variant="primary"
                        onClick={() => {
                            if (canValidate) handleValidate();
                        }}
                    >
                        {t("Validate")}
                    </CustomMainColorButton>
                </React.Fragment>
            </ModalFooter>
        </StyledModal>
    );
}
